import React, { useState, useMemo, useCallback, useEffect } from "react";
import Button from "@mui/material/Button";
import { Typography, TextField } from "@material-ui/core";
import { file_icon } from "assets/images/calender";
import ClassTableThumnail from "components/items/ClassTableThumnail";
import CardContent from "@mui/material/CardContent";
import { TagList, TeacherInfoItem, ClassTitle } from "components/items";
import usePopAlert from "components/popAlert/hooks/usePopAlert";
import usePopAlert2 from "components/popAlert/hooks/usePopAlert2";
import {
  Desktop,
  TabletAndMobile,
  Tablet,
  AllMobile,
} from "components/utils/MediaQuery";
import { useNavigate } from "react-router";
import useBigCalendarClassItem from "./hooks/useBigCalendarClassItem";
import moment from "moment";
import "moment/locale/ko";
import { API_BASE_URL } from "common/constants";
import { ModalWrap, ModalType1 } from "../modal";
import useModalWrapper from "components/modal/hooks/useModalWrapper";
import { useInput } from "common/hooks";
import TogetherScheduleTable from "./TogetherScheduleTable";

// 상수
const SUPERPOT_COURSES = ["CT01", "CT02"];
const SUPERPOT_NATIVE_COURSES = ["CT02", "CT04"];
const PROFILE_NAME_REGEX = /^[가-힣a-zA-Z]{1,5}$/;
const ORDER_STATUS_CANCELED = 20;

// 유틸리티 함수
const formatTime = (time) => {
  if (!time) return "";
  const hour = time.substring(0, 2);
  const min = time.substring(2);
  return hour > 12 ? `오후 0${hour - 12}:${min}` : `오전 ${hour}:${min}`;
};

const isSuperpotCourse = (courseCode) => SUPERPOT_COURSES.includes(courseCode);

const getGradeLabel = (gradeCode) => {
  const code = String(gradeCode);
  const gradeMap = {
    10: "예비초",
    11: "초등 1학년",
    12: "초등 2학년",
    13: "초등 3학년",
    14: "초등 4학년",
    15: "초등 5학년",
    16: "초등 6학년",
  };
  return gradeMap[code] ?? gradeCode ?? "학년 없음";
};

function BigCalendarClassItem({ key, classInfo, user, handlePostDownload }) {
  const { showAlert, showConfirm } = usePopAlert();
  const { showAlert2 } = usePopAlert2();
  const { buttons } = classInfo;
  const {
    materials,
    materialsModal,
    handleDownloadClick,
    handleEnterClick,
    applyProfileEdit,
    applyScheduleEdit,
    applySuperpotScheduleEdit,
  } = useBigCalendarClassItem(classInfo);
  const navigate = useNavigate();
  const together = classInfo.together;
  const superpot = classInfo.superpot;
  const scheduleModalHandler = useModalWrapper();
  const profileModalHandler = useModalWrapper();
  const profileName = useInput(
    classInfo.bookclub_profile_name || classInfo.bookclub_child_member_name,
  );
  const scheduleModal = useModalWrapper();
  const [error, setError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [lectureSeq, setLectureSeq] = useState("");
  const [asisStudyRoomSeq, setAsisStudyRoomSeq] = useState("");
  const [tobeStudyRoomSeq, setTobeStudyRoomSeq] = useState("");
  const [changeScheduleType, setChangeScheduleType] = useState("");
  const [superpotSchedule, setSuperpotSchedule] = useState();
  const [allTodayStudyTime, setAllTodayStudyTime] = useState(null);
  const [allNextLessonDate, setAllNextLessonDate] = useState(null);
  const [allNextLessonTime, setAllNextLessonTime] = useState(null);
  const [allTodayStudyYn, setAllTodayStudyYn] = useState(null);

  const isTogetherClass = classInfo.class_type === 9;
  const isSuperPotCourse = isSuperpotCourse(classInfo.together_course_code);

  const isCanceledClass = useMemo(
    () =>
      !classInfo?.order_status ||
      classInfo.order_status === ORDER_STATUS_CANCELED,
    [classInfo?.order_status],
  );

  // together courseCode CT02 / CT04는 6시간전까지 차일 이후로 변경 가능 / 당일수업은 x
  // 수업 1시간 전 : 1 / 수업중 : 2 / 수업종료 후 : 3 (default : 0)
  useEffect(() => {
    const source = isSuperPotCourse ? superpot : together;
    setAllTodayStudyTime(source?.isTodayStudyTime);
    setAllNextLessonDate(source?.nextLessonDate);
    setAllNextLessonTime(source?.nextLessonTime);
    setAllTodayStudyYn(source?.isTodayStudyYn);
  }, [together, superpot, isSuperPotCourse]);

  const classEnterText = useMemo(() => {
    if (buttons?.close_yn || buttons?.finish_yn) {
      return "클래스 종료";
    }

    if (
      !classInfo.schedule_date ||
      !classInfo.schedule_time ||
      classInfo.order_status === 20 ||
      buttons?.enter_room_yn ||
      classInfo.abnormal_yn
    ) {
      return "클래스 입장";
    }

    const scheduleDate = moment(classInfo.schedule_date + " 0000");
    const currentDate = moment(moment().format("YYYYMMDD") + " 0000");
    if (currentDate.isSame(classInfo.schedule_date)) {
      return "클래스 입장";
    }

    const durationDays = moment
      .duration(scheduleDate.diff(currentDate))
      .asDays();

    return durationDays > 0
      ? `클래스 입장 (D-${Math.ceil(durationDays)})`
      : "클래스 입장";
  }, [
    buttons?.close_yn,
    buttons?.finish_yn,
    buttons?.enter_room_yn,
    classInfo.schedule_date,
    classInfo.schedule_time,
    classInfo.order_status,
    classInfo.abnormal_yn,
  ]);

  const onDownloadClick = useCallback(() => {
    if (buttons?.download_yn) handleDownloadClick();
  }, [buttons?.download_yn, handleDownloadClick]);

  const onFeedbackClick = useCallback(() => {
    if (!buttons?.feedback_yn) return;
    navigate(`/feedback/${classInfo.classroom_seq}`, {
      state: {
        classSeq: classInfo.class_seq,
        classmateSeq: classInfo.classmate_seq,
        userSeq: classInfo.user_seq,
      },
    });
  }, [
    buttons?.feedback_yn,
    navigate,
    classInfo.classroom_seq,
    classInfo.class_seq,
    classInfo.classmate_seq,
    classInfo.user_seq,
  ]);

  const onEnterRoomClick = useCallback(() => {
    handleEnterClick(buttons);
  }, [handleEnterClick, buttons]);

  const onMaterialClick = useCallback(
    (item) => {
      const downloadMaterial = () => {
        window.open(
          `${API_BASE_URL}/classes/${classInfo.class_seq}/material-download/${item.material_file_seq}?order_seq=${classInfo.order_seq}`,
          "_blank",
        );
        handlePostDownload?.(classInfo.order_seq);
      };

      if (buttons.downloaded_yn) {
        downloadMaterial();
      } else {
        showConfirm(
          "알림",
          "수업 자료 다운로드 시 클래스 취소가 불가합니다. 자료를 다운로드 하시겠습니까?",
          { confirmHandler: downloadMaterial },
        );
      }
    },
    [
      buttons.downloaded_yn,
      classInfo.class_seq,
      classInfo.order_seq,
      handlePostDownload,
      showConfirm,
    ],
  );

  const onScheduleChange = scheduleModalHandler.handleOpen;
  const onProfileClick = profileModalHandler.handleOpen;

  const handleProfileName = useCallback(
    (e) => {
      const name = e.target.value;
      profileName.handleChange(e);

      if (name.length > 5) {
        setError(true);
        setErrorMsg("※ 5글자 이내로 입력해 주세요.");
      } else if (!PROFILE_NAME_REGEX.test(name)) {
        setError(true);
        setErrorMsg("※ 한글, 영어 외의 다른 문자는 입력할 수 없습니다.");
      } else {
        setError(false);
        setErrorMsg("");
      }
    },
    [profileName],
  );

  const handleApplyProfile = useCallback(() => {
    if (error) {
      showAlert("알림", "프로필명 형식을 확인 후에 다시 시도해주세요.");
      return;
    }
    applyProfileEdit(profileName.state, classInfo.order_seq);
    profileModalHandler.handleClose();
    window.location.reload();
  }, [
    error,
    showAlert,
    applyProfileEdit,
    profileName.state,
    classInfo.order_seq,
    profileModalHandler,
  ]);

  const handleSchedule = useCallback(
    (changeType) => {
      const isScheduleDateDifferent = !moment(superpot?.scheduleDate).isSame(
        superpot?.nextLessonDate,
      );

      if (changeType === "A" && isSuperPotCourse && isScheduleDateDifferent) {
        const nextLessonStr = moment(superpot?.nextLessonDate).format(
          "YYYY년 MM월 DD일",
        );
        showAlert(
          "알림",
          `계속 변경은 ${nextLessonStr}\n 스케줄만 가능합니다.`,
        );
        return;
      }

      scheduleModalHandler.handleClose();
      scheduleModal.handleOpen();
      setChangeScheduleType(changeType);
    },
    [scheduleModalHandler, scheduleModal],
  );

  const handleChangeScheduleApply = useCallback(() => {
    if (isSuperPotCourse || isSuperpotCourse(classInfo?.together_course_code)) {
      if (superpot && superpotSchedule) {
        applySuperpotScheduleEdit(
          superpot.sapOrderNo, // sapOrderNo
          superpot.scheduleDate, // fromScheduleDate
          superpotSchedule.toScheduleDate, // toScheduleDate
          superpot.classroomSeq, // fromClassroomSeq
          superpotSchedule.classroomSeq, // toClassroomSeq
          superpot.progressSeq, // fromProgressSeq
          superpot.progressSeq, // toProgressSeq
          superpotSchedule.dayCode, // dayCode
          changeScheduleType === "B" ? "OS02" : "OS01", // scheduleType
          superpot.scheduleSeq, // fromScheduleSeq
          classInfo.together_course_code, // courseType
          moment(superpot.scheduleDate).format("YYYYMM"), // scheduleMonth
        );
      }
    } else {
      applyScheduleEdit(
        lectureSeq,
        asisStudyRoomSeq,
        tobeStudyRoomSeq,
        changeScheduleType,
      );
    }
    scheduleModal.handleClose();
  }, [
    isSuperPotCourse,
    classInfo?.together_course_code,
    superpot,
    superpotSchedule,
    applySuperpotScheduleEdit,
    together.courseCode,
    applyScheduleEdit,
    lectureSeq,
    asisStudyRoomSeq,
    tobeStudyRoomSeq,
    changeScheduleType,
    scheduleModal,
  ]);
  const scheduleStatus = useMemo(() => {
    if (allTodayStudyYn === "N" || !allTodayStudyTime) return 0;

    const nowTime = moment();
    const classTime = classInfo?.class_time;
    const isSuperPotNative = SUPERPOT_NATIVE_COURSES.includes(
      together?.courseCode,
    );
    const todayStudyTime = moment(allTodayStudyTime, "HHmm");
    const studyBeforeTime = todayStudyTime
      .clone()
      .subtract(isSuperPotNative ? 6 : 1, "hours");
    const studyEndTime = todayStudyTime.clone().add(classTime, "minutes");

    if (nowTime.isBefore(studyBeforeTime)) return 1;
    if (nowTime.isBetween(studyBeforeTime, studyEndTime)) return 2;
    if (nowTime.isAfter(studyEndTime)) return 3;
    return 0;
  }, [
    allTodayStudyYn,
    allTodayStudyTime,
    classInfo?.class_time,
    together?.courseCode,
  ]);

  const isTodayTogetherStudy = useMemo(
    () => allTodayStudyYn === "Y",
    [allTodayStudyYn],
  );

  const onGradeClick = useCallback(() => {
    const label = getGradeLabel(
      together?.togetherGradeCode ?? classInfo.together_grade_code,
    );
    showAlert2(
      "투게더 학년은",
      `<div style="text-align: center;">
        ${label} 입니다.
        <div style="margin-top: 20px; color: #666; font-size: 14px; text-align: center;">
          ※ 학년 변경 문의는 담당 북큐레이터에게 진행해 주세요.
        </div>
      </div>`,
    );
  }, [together?.togetherGradeCode, classInfo.together_grade_code, showAlert2]);

  const onSuperpotClick = useCallback(() => {
    if (!classInfo?.superpot?.progressStages?.length) {
      showAlert("알림", "데이터가 없습니다.");
      return;
    }

    const stagesList = classInfo.superpot.progressStages
      .map(
        (item) =>
          `<div style="margin: 8px 0; font-size: 14px;">
            <span style="color: #0c8eff; font-weight: 600;">
              ${moment(item.baseMonth, "YYYYMM").format("YYYY년 MM월")}
            </span>
            <span style="font-weight: 500; margin-left: 12px;">
             ${item.progressCode}
            </span>
          </div>`,
      )
      .join("");

    showAlert2(
      "수업 단계/이력 확인",
      `<div style="text-align: center;">
        ${stagesList}
        <div style="margin-top: 20px; color: #666; font-size: 14px; text-align: center;">
          ※ 단계/승급 문의는 담당 북큐레이터에게 진행해주세요.
        </div>
      </div>`,
    );
  }, [classInfo?.superpot?.progressStages, showAlert, showAlert2]);

  // 스케줄 변경 비활성화 여부
  const isScheduleChangeDisabled =
    (isSuperPotCourse
      ? superpot?.isMonthlyArrangeEnd
      : together?.isMonthlyArrangeEnd) === "N";

  // 다음 수업 표시 - 오늘 수업인 경우
  const todayLessonDisplay = useMemo(() => {
    if (classInfo.lep_order_no === null) {
      return <span>차일 중으로 생성될 예정</span>;
    }
    return (
      <span>
        {moment(allTodayStudyTime ? new Date() : allNextLessonDate).format(
          "YYYY-MM-DD(ddd)",
        )}
        {formatTime(allTodayStudyTime || allNextLessonTime)}
      </span>
    );
  }, [
    classInfo.lep_order_no,
    allTodayStudyTime,
    allNextLessonDate,
    allNextLessonTime,
  ]);

  // 다음 수업 표시 - 일반
  const nextLessonDisplay = useMemo(() => {
    if (classInfo.lep_order_no === null || !allNextLessonDate) {
      return <span>차일 중으로 생성될 예정</span>;
    }
    return (
      <span>
        {moment(allNextLessonDate).format("YYYY-MM-DD(ddd) ")}
        {formatTime(allNextLessonTime)}
      </span>
    );
  }, [classInfo.lep_order_no, allNextLessonDate, allNextLessonTime]);

  // 스케줄 변경 버튼 그룹
  const renderScheduleChangeButtons = () => (
    <div className="btnBox">
      <Button
        className="changeSchedule-btn button-square-type btn-color-type2"
        variant="outlined"
        onClick={() => handleSchedule("B")}
      >
        다음 수업만 변경
        <TabletAndMobile>
          <br />
        </TabletAndMobile>
        (1회)
      </Button>
      <Button
        className="changeSchedule-btn button-square-type btn-color-type2"
        variant="outlined"
        onClick={() => handleSchedule("A")}
      >
        모든 수업 일정 변경
      </Button>
    </div>
  );

  return (
    <>
      {/*  2022-11-16  수업자료 다운로드 리스트 팝업 생성 */}
      <ModalWrap
        className="option-change-btn showListModal"
        modalinfo="other-button"
        visible={materialsModal.visibleModal}
        handleOpen={materialsModal.handleOpen}
        handleClose={materialsModal.handleClose}
      >
        <ModalType1 title="수업자료">
          <ul>
            {materials?.length > 0 &&
              materials.map((item) => (
                <li
                  key={item.material_file_seq}
                  onClick={() => onMaterialClick(item)}
                >
                  <a>
                    {item.file_original_name?.slice(
                      0,
                      item.file_original_name?.lastIndexOf("."),
                    )}
                  </a>
                </li>
              ))}
          </ul>
        </ModalType1>
      </ModalWrap>

      {/* 프로필명 변경 팝업 */}
      <ModalWrap
        className="-profileEdit"
        modalinfo="other-button"
        visible={profileModalHandler.visibleModal}
        handleOpen={profileModalHandler.handleOpen}
        handleClose={profileModalHandler.handleClose}
      >
        <ModalType1
          title="프로필 수정"
          buttonCancel="취소"
          buttonApply="적용"
          handleApply={handleApplyProfile}
        >
          <>
            <TextField
              type="text"
              name="name"
              placeholder="프로필명(수정 가능)"
              variant="outlined"
              color="primary"
              value={profileName.state}
              onChange={(e) => {
                handleProfileName(e);
              }}
              style={{
                width: TabletAndMobile ? "100%" : "300px",
                marginBottom: 20,
              }}
              error={error}
              helperText={errorMsg}
            />
          </>
        </ModalType1>
      </ModalWrap>

      {/* 투게더 스케줄 변경 팝업 */}
      <ModalWrap
        className="-changeSchedule"
        modalinfo="other-button"
        visible={scheduleModalHandler.visibleModal}
        handleOpen={scheduleModalHandler.handleOpen}
        handleClose={scheduleModalHandler.handleClose}
      >
        <ModalType1
          title={
            allTodayStudyYn === "Y" ? "스케줄 변경 안내" : "스케줄 조회/변경"
          }
        >
          <>
            {allTodayStudyYn === "Y" ? (
              <>
                {scheduleStatus === 2 ? (
                  <Typography
                    variant="h6"
                    component="h6"
                    style={{
                      textAlign: "center",
                      color: "#1c1c1c",
                      fontSize: "18px",
                      padding: "30px",
                    }}
                  >
                    수업 1시간 전부터는 일정을 바꿀 수 없습니다.{" "}
                    <span
                      style={{
                        fontWeight: "bold",
                        textDecoration: "underline",
                        color: "#1c1c1c",
                      }}
                    >
                      <br />
                      수업 종료 후
                    </span>
                    에 다시 시도해 주세요😥
                  </Typography>
                ) : (
                  <>
                    <Typography
                      variant="h6"
                      component="h6"
                      className="guide-tit"
                    >
                      다음 수업 예정일은 <br />
                      {scheduleStatus === 1 ? (
                        todayLessonDisplay
                      ) : classInfo.lep_order_no !== null ? (
                        classInfo.together_course_code === "CT01" ||
                        classInfo.together_course_code === "CT02" ? (
                          <span>
                            {moment(superpot?.nextLessonDate).format(
                              "YYYY-MM-DD(ddd)",
                            )}
                            {formatTime(together?.nextLessonTime)}
                          </span>
                        ) : (
                          <span>
                            {moment(together?.nextLessonDate).format(
                              "YYYY-MM-DD(ddd)",
                            )}
                            {formatTime(together?.nextLessonTime)}
                          </span>
                        )
                      ) : (
                        <span>차일 중으로 생성될 예정</span>
                      )}
                      입니다.
                    </Typography>
                    {isScheduleChangeDisabled ? (
                      <Typography
                        variant="h6"
                        component="h6"
                        className="guide-tit"
                      >
                        첫 수업일 변경은 <br />
                        <span>다음달 1일 오후 1시 이후</span>에 가능합니다.
                      </Typography>
                    ) : (
                      renderScheduleChangeButtons()
                    )}
                  </>
                )}
              </>
            ) : (
              <>
                <Typography variant="h6" component="h6" className="guide-tit">
                  다음 수업 예정일은 <br />
                  {classInfo.together_course_code === "CT01" ||
                  classInfo.together_course_code === "CT02" ? (
                    <span>
                      {moment(superpot?.nextLessonDate).format(
                        "YYYY-MM-DD(ddd)",
                      )}
                    </span>
                  ) : (
                    <span>{nextLessonDisplay}</span>
                  )}
                  입니다.
                </Typography>
                {isScheduleChangeDisabled ? (
                  <Typography variant="h6" component="h6" className="guide-tit">
                    첫 수업일 변경은 <br />
                    <span>다음달 1일 오후 1시 이후</span>에 가능합니다.
                  </Typography>
                ) : (
                  renderScheduleChangeButtons()
                )}
              </>
            )}
          </>
        </ModalType1>
      </ModalWrap>

      {/* 투게더 스케줄 테이블 팝업 */}
      <ModalWrap
        className="-changeScheduleTable"
        modalinfo="other-button"
        visible={scheduleModal.visibleModal}
        handleOpen={scheduleModal.handleOpen}
        handleClose={scheduleModal.handleClose}
      >
        <ModalType1
          title={`${changeScheduleType === "B" ? "다음 수업만 변경 (1회)" : "모든 수업 일정 변경"}`}
          buttonCancel="취소"
          buttonApply="적용"
          handleApply={handleChangeScheduleApply}
        >
          <TogetherScheduleTable
            orderInfo={classInfo}
            together={classInfo.together}
            setLectureSeq={setLectureSeq}
            setAsisStudyRoomSeq={setAsisStudyRoomSeq}
            setTobeStudyRoomSeq={setTobeStudyRoomSeq}
            changeScheduleType={changeScheduleType}
            scheduleStatus={scheduleStatus}
            superpot={classInfo?.superpot}
            setSuperpotSchedule={setSuperpotSchedule}
          />
        </ModalType1>
      </ModalWrap>

      {/*  //2022-11-16  수업자료 다운로드 리스트 팝업 생성 끝 */}
      <li className="cal-list-item" key={key}>
        <div className="cal-grid">
          <Typography variant="body2" component="p" className="cal-list-p">
            <span className="cal-list-time">
              {moment("20220101 " + classInfo.schedule_time).format("A hh:mm")}
            </span>
          </Typography>
          {classInfo.class_type !== 9 && (
            <div className="profile-item">
              <Typography variant="body2" component="p" className="profile-p">
                {classInfo.user_name}
              </Typography>
              <div className="img">
                <img
                  src={classInfo.user_profile_image_url}
                  alt="학생 이름"
                  onError={(e) => (e.target.src = "/dummy/dummyteach.png")}
                />
              </div>
            </div>
          )}
        </div>
        <div className="cal-list-info-wrap">
          <div className="cal-list-img">
            <ClassTableThumnail data={classInfo} />
          </div>
          <div className="cal-list-text">
            <CardContent className="card-content-wrap">
              <div className="content-top">
                <TagList data={classInfo} />
              </div>
              <ClassTitle data={classInfo} />

              {classInfo.together_course_code === "MATH" ? (
                <Typography
                  variant="h3"
                  component="h3"
                  style={{
                    fontFamily: "NotoSansMedium",
                    fontSize: 14,
                    fontWeight: "bold",
                    color: "#1c1c1c",
                    marginBottom: 8,
                    textAlign: "left",
                  }}
                >
                  학년{"  "}
                  <button
                    style={{
                      fontFamily: "NotoSansMedium",
                      fontSize: 14,
                      color: "#2F99F4",
                      textDecoration: "underline",
                      fontWeight: "600",
                      marginLeft: 8,
                    }}
                    onClick={onGradeClick}
                  >
                    확인하기
                  </button>
                </Typography>
              ) : (
                !isSuperpotCourse(classInfo.together_course_code) && (
                  <Typography
                    variant="h3"
                    component="h3"
                    style={{
                      fontFamily: "NotoSansMedium",
                      fontSize: 14,
                      fontWeight: "bold",
                      color: "#1c1c1c",
                      marginBottom: 8,
                      textAlign: "left",
                    }}
                  >
                    {`학년  ${getGradeLabel(together?.togetherGradeCode)}`}
                  </Typography>
                )
              )}
              <div className="content-bottom">
                <TeacherInfoItem data={classInfo} showImage={false} />
              </div>
            </CardContent>
          </div>
          <div className="cal-list-button-wrap">
            <div className="cal-list-button-inner">
              {!isTogetherClass ? (
                <>
                  {!isCanceledClass && buttons?.download_yn && (
                    <Button
                      className="cal-list-button cal-list-button-type1 cal-list-button-s1 ml0"
                      onClick={onDownloadClick}
                    >
                      <Desktop>
                        <img src={file_icon} alt="수업 자료" />
                      </Desktop>
                      수업 자료
                    </Button>
                  )}
                  {!isCanceledClass && buttons?.feedback_yn && (
                    <Button
                      className="cal-list-button cal-list-button-type1"
                      onClick={onFeedbackClick}
                    >
                      수업 피드백
                    </Button>
                  )}
                  {!isCanceledClass && (
                    <Button
                      className={`cal-list-button cal-list-button-type2 ${buttons?.enter_room_yn ? "" : "disabled-btn"}`}
                      onClick={onEnterRoomClick}
                    >
                      {classEnterText}
                    </Button>
                  )}
                </>
              ) : (
                <>
                  <Button
                    className="cal-list-button cal-list-button-type2"
                    variant="contained"
                    onClick={onScheduleChange}
                  >
                    스케줄 조회/변경
                  </Button>
                  <Button
                    className="cal-list-button cal-list-button-type2"
                    variant="contained"
                    onClick={onProfileClick}
                  >
                    프로필 조회/수정
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
        {classInfo.class_type === 9 && (
          <div
            className="cal-list-note"
            style={{ textAlign: "left", marginTop: 10, lineHeight: 1.2 }}
          >
            <p style={{ fontSize: 12 }}>
              ※ 수업 입장은 [북패드 &gt; 투게더 수업] 메뉴에서 가능합니다.
            </p>
            <p style={{ fontSize: 12 }}>
              ※ 수업 일정이 정확하지 않을 수 있으니, 북패드 내 투게더 시간표를
              꼭 확인해 주세요!{" "}
            </p>
          </div>
        )}
        {classInfo?.superpot ? (
          <div className="cal-list-superpot-note">
            <p className="lbl">수업</p>
            <div onClick={onSuperpotClick}>단계 확인하기</div>
          </div>
        ) : null}
      </li>
    </>
  );
}

export default BigCalendarClassItem;
